import { useId, useMemo, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { DialogContent, Skeleton } from '@mui/material';
import { getObjectChanges } from '@/Infrastructure/Utilities/object.type';
import { getApiFormFieldErrors } from '@api/getApiError';
import { useFetchLegQuery, useSetScheduleMutation } from '@api/Trinium/Endpoints/Leg/slice';
import FormikModal from '@components/Modals/FormikModal';
import FormikModalHeader from '@components/Modals/FormikModalHeader';
import FormikModalHeaderSkeleton from '@components/Modals/FormikModalHeaderSkeleton';
import { useModalProvider } from '@components/Modals/ModalContext';
import LegOrderHeader from '@components/Modals/Headers/LegOrderHeader';
import LegOrderHeaderSkeleton from '@components/Modals/Headers/LegOrderHeaderSkeleton';
import { setFormFieldError } from '@forms/Components/types';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { isFormikErrorsEmpty } from '@hooks/useFormikValues';
import FormikForm from './FormikForm';
import UpdateScheduledFormSkeleton from './FormSkeleton';
import { updateScheduledPuDlToApiRaw } from './maps';
import { updateScheduledPuDlApiFieldMappings, UpdateScheduledPuDlFormik, UpdateScheduledPuDlProps } from './types';

/**
 * Local function used for rendering the content of the Update Scheduled Pickup Delivery modal
 */
export default function Modal({ legIds }: UpdateScheduledPuDlProps) {
	const formId = useId();
	const [isOpen, setIsOpen] = useState(true);
	const { number: terminalNumber } = useCurrentTerminal();
	const modalContext = useModalProvider();
	const { queueModalAnnouncement } = modalContext;
	const [setSchedule] = useSetScheduleMutation();

	const { data: leg, isFetching } = useFetchLegQuery(
		{
			terminalNumber: terminalNumber,
			id: legIds[0]
		},
		{ refetchOnMountOrArgChange: true }
	);

	async function onFormSubmit(values: UpdateScheduledPuDlFormik, formikHelpers: FormikHelpers<UpdateScheduledPuDlFormik>) {
		const validationErrors = await formikHelpers.validateForm(values);

		if (legIds.length > 0 && isFormikErrorsEmpty(validationErrors) && initialValues) {
			const valuesToPost: Partial<UpdateScheduledPuDlFormik> = getObjectChanges(values, initialValues);

			await setSchedule({
				terminalNumber,
				actionType: {
					data: {
						legIds: legIds,
						...updateScheduledPuDlToApiRaw(valuesToPost)
					}
				}
			})
				.unwrap()
				.then(() => {
					/** On Success, close the modal */
					setIsOpen(false);
				})
				.catch((ex: unknown) => {
					const errors = getApiFormFieldErrors(updateScheduledPuDlApiFieldMappings, ex);

					errors.forEach((error) => {
						if (error.fieldName) {
							setFormFieldError(error.fieldName, error.errorMessage, formikHelpers.setFieldError);
						} else {
							queueModalAnnouncement({
								severity: 'error',
								message: error.errorMessage
							});
						}
					});
				});
		}
	}

	const initialValues: UpdateScheduledPuDlFormik | undefined = useMemo(() => {
		if (!leg || isFetching) return undefined;

		// form should not have initial values if more than Leg is selected
		if (legIds.length !== 1) {
			return {
				scheduledPickupDateRange: null,
				scheduledDeliveryDateRange: null,
				removeScheduledDelivery: false,
				removeScheduledPickup: false
			};
		}

		return {
			scheduledPickupDateRange: leg.scheduledPickupDateFrom
				? `${leg.scheduledPickupDateFrom.dateTime}${leg.scheduledPickupDateTo?.time ? ` - ${leg.scheduledPickupDateTo.time}` : ''}`
				: null,
			scheduledDeliveryDateRange: leg.scheduledDeliveryDateFrom
				? `${leg.scheduledDeliveryDateFrom.dateTime}${leg.scheduledDeliveryDateTo?.time ? ` - ${leg.scheduledDeliveryDateTo.time}` : ''}`
				: null,
			removeScheduledDelivery: false,
			removeScheduledPickup: false
		};
	}, [leg, legIds, isFetching]);

	return (
		<FormikModal
			isOpen={isOpen}
			onClose={() => {
				setIsOpen(false);
			}}
		>
			{initialValues ? (
				<Formik
					initialValues={initialValues}
					onSubmit={onFormSubmit}
				>
					<>
						<FormikModalHeader
							formName={formId}
							onClose={() => {
								setIsOpen(false);
							}}
							title='Scheduled Date and Time'
						/>

						{legIds.length === 1 && (
							<LegOrderHeader
								terminalNumber={terminalNumber}
								legId={legIds[0]}
							/>
						)}

						<DialogContent>
							{isFetching || !leg ? (
								<Skeleton
									variant='rounded'
									width={200}
									height={100}
								/>
							) : (
								<FormikForm id={formId} />
							)}
						</DialogContent>
					</>
				</Formik>
			) : (
				<>
					<FormikModalHeaderSkeleton
						title='Scheduled Date and Time'
						onClose={() => {
							setIsOpen(false);
						}}
					/>
					<LegOrderHeaderSkeleton />
					<DialogContent>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								rowGap: '8px'
							}}
						>
							<div style={{ display: 'flex' }}>
								<UpdateScheduledFormSkeleton />
							</div>
						</div>
					</DialogContent>
				</>
			)}
		</FormikModal>
	);
}
